// Shared constants/helpers for the "connected deal" session rules:
//  - signing out (manually or via the idle timeout) while connected to a deal
//    tells the counterpart and sends them home; the deal itself stays active
//  - leaving a connected deal from any page asks for confirmation first
// The runtime lives in components/ds/DealSessionManager.jsx.

// Dispatched by useAuth().logout BEFORE the token is cleared. Listeners push
// promises onto event.detail.tasks; logout waits (bounded) for them so the
// "left the deal" message and the expiry refresh go out while the session is
// still valid.
export const BEFORE_LOGOUT_EVENT = "klargo:before-logout";

export async function runBeforeLogout(timeoutMs = 3000) {
  const tasks = [];
  try {
    window.dispatchEvent(new CustomEvent(BEFORE_LOGOUT_EVENT, { detail: { tasks } }));
  } catch (_) {
    return;
  }
  if (!tasks.length) return;
  await Promise.race([
    Promise.allSettled(tasks),
    new Promise((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}

// SignalR message payloads: `${PREFIX}<reason>:<senderClientId>`. The sender id
// (one per browser tab) lets a tab ignore the echo of its own message without
// needing to know its own role.
export const DEAL_LEFT_PREFIX = "klargo-deal-left:";

export const DEAL_LEFT_LOGOUT = "logout";
export const DEAL_LEFT_LEAVE = "leave";

// The idle timeout, as opposed to a deliberate sign-out. The counterpart is offered a different
// choice for each: a timed-out party can come back, so the other side is asked whether to wait.
export const DEAL_LEFT_TIMEOUT = "timeout";

// Both go through useAuth().logout, which cannot tell them apart on its own, so
// SessionTimeoutManager marks the one it causes just before calling it. Read once, by the
// BEFORE_LOGOUT_EVENT listener that builds the message.
let idleLogout = false;
export const markIdleLogout = () => {
  idleLogout = true;
};
export const takeIdleLogout = () => {
  const was = idleLogout;
  idleLogout = false;
  return was;
};

// The seller cancelled the sale: unlike "leave", the deal is over, so the counterpart is told so.
export const DEAL_LEFT_CANCEL = "cancel";

// Fired by the pages that cancel a sale (the QR step, the transaction record) AFTER the server
// has cancelled it. DealSessionManager tells the buyer over SignalR and drops this tab's deal
// connection, so the "Leave the deal?" guard stops applying. Resolves when that is done.
export const DEAL_CANCELLED_EVENT = "klargo:deal-cancelled";

// Fired by DealSessionManager once this user has been told a deal is over (cancelled by the other
// party, or expired) so a page showing that deal, such as the overview, can reload and drop it.
export const DEAL_ENDED_EVENT = "klargo:deal-ended";
export const announceDealEnded = () => {
  try {
    window.dispatchEvent(new CustomEvent(DEAL_ENDED_EVENT));
  } catch (_) {}
};

export async function announceDealCancelled(dealId, timeoutMs = 4000) {
  const tasks = [];
  try {
    window.dispatchEvent(new CustomEvent(DEAL_CANCELLED_EVENT, { detail: { dealId, tasks } }));
  } catch (_) {
    return;
  }
  if (!tasks.length) return;
  await Promise.race([Promise.allSettled(tasks), new Promise((resolve) => setTimeout(resolve, timeoutMs))]);
}

// The sale is complete (the seller has reported the transfer of ownership). The deal is over, so the
// live connection between the two parties is closed: nothing either of them does afterwards - leaving
// the page, signing out - may reach the other as a message about the deal. Fired by the ownership
// stage, for the seller who reported and for the buyer who finds it reported.
export const DEAL_COMPLETED_EVENT = "klargo:deal-completed";
export const announceDealCompleted = (dealId) => {
  try {
    window.dispatchEvent(new CustomEvent(DEAL_COMPLETED_EVENT, { detail: { dealId } }));
  } catch (_) {}
};

export const buildDealLeftMessage = (reason, clientId) => `${DEAL_LEFT_PREFIX}${reason}:${clientId}`;

export function parseDealLeftMessage(message) {
  if (typeof message !== "string" || !message.startsWith(DEAL_LEFT_PREFIX)) return null;
  const [reason, clientId] = message.slice(DEAL_LEFT_PREFIX.length).split(":");
  return { reason, clientId };
}

// Where both parties are sent when the deal is left/ended by either side: the
// signed-in home page. (/Home is the public landing page and looks logged out.)
export const DEAL_HOME_PATH = "/user/ChooseAction";

// A deal is considered "left" by pages that are not part of the deal flow.
// These routes are the deal flow itself (navigating between them is normal).
export const DEAL_FLOW_PATH_PREFIXES = ["/SellerProfile", "/BuyerProfile", "/BuyerSuccess", "/Buyer", "/Contract", "/Payment", "/Ownership", "/Insurance"];

export const isDealFlowPath = (pathname) =>
  DEAL_FLOW_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
